import * as THREE from 'three';
import { FikoObje3D } from '../../../FikoObje3D';
import { FikoSahne } from '../../../FikoSahne';
import { FikoVektor3D } from '../../../FikoVektor3d';
import { FikoVektorTakimYol3D } from '../../../FikoThreeCam/FikoVektorTakimYol3d';
import { FikoTakimFrezeleme } from '../../../FikoThreeCam/FikoTakimFrezeleme';
import { offsetHat } from '../../../Fiko2dPathSihirbaz';

/**
 * FikoAktiviteGravur - 2D CAM Gravür/Kazıma Aktivitesi
 *
 * Profesyonel CAM sistemleri (Mastercam, Fusion 360, VCarve Pro) standartlarında
 * eksiksiz gravür/kazıma motoru.
 *
 * Desteklenen Gravür Tipleri:
 * - CizgiGravur: Sabit derinlikte çizgi kazıma (line engraving)
 * - VGravur: V-kesici ile değişken derinlik (VCarve)
 * - DolguGravur: Alan doldurma (fill/pocket engraving)
 * - NoktaGravur: Nokta/stipple gravür
 *
 * Desteklenen Dolgu Desenleri:
 * - Offset: İçe doğru offset spiral
 * - Raster: Paralel çizgiler (yatay/dikey/açılı)
 * - CrossHatch: Çapraz çizgiler
 * - Spiral: Merkeze doğru spiral
 *
 * @author FikoYT
 * @version 1.0.0 - Professional Engraving Engine
 */
export class FikoAktiviteGravur extends FikoObje3D {
    static SAYAC = 1;

    override type = 'FikoAktiviteGravur';
    readonly isGroup: true;

    // ═══════════════════════════════════════════════════════════════════
    // SAHNE REFERANSİ VE DİALOG
    // ═══════════════════════════════════════════════════════════════════

    sahne?: FikoSahne;
    private _dialogAciliyor = false;
    private _acikDialog: any;

    // ═══════════════════════════════════════════════════════════════════
    // GRAVÜR TİPİ
    // ═══════════════════════════════════════════════════════════════════

    gravurTipi: 'CizgiGravur' | 'VGravur' | 'DolguGravur' | 'NoktaGravur' = 'CizgiGravur';

    // ═══════════════════════════════════════════════════════════════════
    // GRAVÜR PROFİLLERİ
    // ═══════════════════════════════════════════════════════════════════

    profiller: FikoVektor3D[][] = [];              // Gravür profilleri (çizgiler)
    metinIcerik: string = '';                      // Metin içeriği (text engraving için)
    fontAdi: string = 'Arial';                     // Font adı
    fontSize: number = 10;                         // Font boyutu (mm)
    fontKalinlik: 'Normal' | 'Bold' = 'Normal';    // Font kalınlığı

    // ═══════════════════════════════════════════════════════════════════
    // DÜZLEM TANIMLARI
    // ═══════════════════════════════════════════════════════════════════

    ustZ: number = 0;                              // Parça üst yüzeyi
    gravurDerinlik: number = 0.5;                  // Gravür derinliği (mm)
    guvenliZ: number = 5;                          // Güvenli yükseklik

    // ═══════════════════════════════════════════════════════════════════
    // TAKIM
    // ═══════════════════════════════════════════════════════════════════

    takim: FikoTakimFrezeleme;
    takimYonu: FikoVektor3D = new FikoVektor3D(0, 0, 1);

    // ═══════════════════════════════════════════════════════════════════
    // ÇİZGİ GRAVÜR PARAMETRELERİ (LINE ENGRAVING)
    // ═══════════════════════════════════════════════════════════════════

    cizgiDerinlik: number = 0.3;                   // Sabit çizgi derinliği (mm)
    cizgiGenislik: number = 0.5;                   // Çizgi genişliği (mm)
    cizgiOffset: 'Merkez' | 'Sol' | 'Sag' = 'Merkez';  // Offset yönü

    // ═══════════════════════════════════════════════════════════════════
    // V-GRAVÜR PARAMETRELERİ (VCARVE)
    // ═══════════════════════════════════════════════════════════════════

    vAci: number = 60;                             // V-kesici açısı (derece) - 30, 45, 60, 90
    vMaxDerinlik: number = 3;                      // Maximum V derinliği (mm)
    vMinDerinlik: number = 0.1;                    // Minimum V derinliği (mm)
    vFlatDerinlik: number = 0;                     // Flat bottom derinlik (0 = sadece V)
    vFlatTakim: boolean = false;                   // Flat bottom için ayrı takım
    vFlatTakimCap: number = 3;                     // Flat bottom takım çapı (mm)
    vOtomatikDerinlik: boolean = true;             // Derinliği genişliğe göre otomatik hesapla

    // ═══════════════════════════════════════════════════════════════════
    // DOLGU GRAVÜR PARAMETRELERİ (FILL ENGRAVING)
    // ═══════════════════════════════════════════════════════════════════

    dolguDeseni: 'Offset' | 'Raster' | 'CrossHatch' | 'Spiral' = 'Offset';
    dolguAdim: number = 0.3;                       // Dolgu adımı / hatve (mm)
    dolguAci: number = 0;                          // Raster açısı (derece)
    dolguCrossAci: number = 90;                    // Cross-hatch ikinci açı (derece)
    dolguOverlap: number = 10;                     // Overlap yüzdesi (%)
    dolguCevre: boolean = true;                    // Çevre geçişi yap
    dolguCevreSayisi: number = 1;                  // Çevre geçiş sayısı

    // ═══════════════════════════════════════════════════════════════════
    // NOKTA GRAVÜR PARAMETRELERİ (STIPPLE/DOT)
    // ═══════════════════════════════════════════════════════════════════

    noktaAralik: number = 1;                       // Nokta araları (mm)
    noktaDerinlik: number = 0.2;                   // Nokta derinliği (mm)
    noktaDesen: 'Grid' | 'Hex' | 'Random' = 'Grid';  // Nokta deseni
    noktaBekleme: number = 0.1;                    // Her noktada bekleme (saniye)

    // ═══════════════════════════════════════════════════════════════════
    // HIZ PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    ilerlemeGravur: number = 500;                  // Gravür ilerleme (mm/dk)
    ilerlemeDalma: number = 200;                   // Dalma ilerleme (mm/dk)
    ilerlemeGecis: number = 2000;                  // Geçiş ilerleme (mm/dk)
    hizRapid: number = 10000;                      // Rapid hız (mm/dk)
    devir: number = 12000;                         // Spindle devri (rpm) - gravür için yüksek

    // ═══════════════════════════════════════════════════════════════════
    // GİRİŞ/ÇIKIŞ PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    girisYontemi: 'Dikey' | 'Rampa' | 'Yay' = 'Dikey';
    girisUzunluk: number = 2;                      // Giriş uzunluğu (mm)
    girisRampaAcisi: number = 30;                  // Rampa açısı (derece)

    cikisYontemi: 'Dikey' | 'Rampa' | 'Yay' = 'Dikey';
    cikisUzunluk: number = 2;                      // Çıkış uzunluğu (mm)

    // ═══════════════════════════════════════════════════════════════════
    // OPTİMİZASYON
    // ═══════════════════════════════════════════════════════════════════

    siralama: 'Secimsirasi' | 'EnKisaYol' | 'SoldanSaga' | 'YukaridanAsagi' = 'EnKisaYol';
    grupla: boolean = true;                        // Yakın profilleri grupla
    grupMesafe: number = 5;                        // Gruplama mesafesi (mm)

    // ═══════════════════════════════════════════════════════════════════
    // STAY-DOWN / RETRACT
    // ═══════════════════════════════════════════════════════════════════

    stayDownEtkin: boolean = false;                // Düşük kalma aktif (retract yapma)
    stayDownMesafe: number = 10;                   // Max düşük kalma mesafesi (mm)
    stayDownZ: number = 0.5;                       // Düşük kalma Z offset (mm)

    // ═══════════════════════════════════════════════════════════════════
    // COKLU GEÇİŞ (Multi-Pass)
    // ═══════════════════════════════════════════════════════════════════

    cokluGecisEtkin: boolean = false;              // Çoklu geçiş aktif
    gecisDerinlik: number = 0.2;                   // Her geçişte derinlik (mm)
    gecisPayi: number = 0.05;                      // Son geçiş payı (mm)

    // ═══════════════════════════════════════════════════════════════════
    // METİN GRAVÜR
    // ═══════════════════════════════════════════════════════════════════

    metinHizalama: 'Sol' | 'Orta' | 'Sag' = 'Orta';
    metinAralik: number = 0;                       // Karakter arası (mm)
    metinSatirAralik: number = 2;                  // Satır arası (mm)
    metinYazi: boolean = false;                    // Yazı modu (tek çizgi font)
    metinMirror: boolean = false;                  // Ayna yaz

    // ═══════════════════════════════════════════════════════════════════
    // HESAPLANAN VERİLER
    // ═══════════════════════════════════════════════════════════════════

    takimYoluNoktalar: FikoVektorTakimYol3D[] = [];
    hesaplananSure: number = 0;
    toplamMesafe: number = 0;

    // ═══════════════════════════════════════════════════════════════════
    // 3D GÖRÜNTÜLEME
    // ═══════════════════════════════════════════════════════════════════

    takimYoluMesh: THREE.Group;
    profilMesh: THREE.Group;

    constructor(sahne?: FikoSahne) {
        super(sahne);
        this.sahne = sahne;
        this.name = `Gravur-${FikoAktiviteGravur.SAYAC++}`;
        this.takim = new FikoTakimFrezeleme(sahne);
        this.takim.takimTipi = 'VKesici';
        this.takim.cap = 3;
        this.takimYoluMesh = new THREE.Group();
        this.profilMesh = new THREE.Group();
        this.add(this.takimYoluMesh);
        this.add(this.profilMesh);
    }

    // ═══════════════════════════════════════════════════════════════════
    // DİALOG DÜZENLEME
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Gravür aktivitesini düzenlemek için dialog açar
     */
    async duzenle($event?: any): Promise<void> {
        if (this._dialogAciliyor) {
            console.warn('[FikoAktiviteGravur] Dialog zaten açılıyor...');
            return;
        }

        if (!this.sahne?.anaGosterim?.DialogFiko) {
            console.warn('[FikoAktiviteGravur] DialogFiko servisi bulunamadı');
            return;
        }

        const dialogId = 'fiko-gravur-edit-' + this.uuid;
        const dialogService = this.sahne.anaGosterim.DialogFiko;

        // Açık dialog kontrolü
        const mevcutDialog = dialogService.idIleAl?.(dialogId);
        if (mevcutDialog && mevcutDialog.miAcik) {
            this._acikDialog = mevcutDialog;
            return;
        }

        try {
            this._dialogAciliyor = true;

            // Dialog bileşenini lazy import et
            const { DialogFikoGravurComponent } = await import('./dialog-fiko-gravur');

            // Dialog'u aç
            const dialog = dialogService.ac({
                bilesen: DialogFikoGravurComponent,
                id: dialogId,
                $event: $event,
                ayarlar: {
                    baslik: `Gravür: ${this.name}`,
                    simge: 'edit'
                },
                parametreler: {
                    aktivite: this
                }
            });

            this._acikDialog = dialog;
        } catch (error) {
            console.error('[FikoAktiviteGravur] Dialog açılırken hata:', error);
        } finally {
            this._dialogAciliyor = false;
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // PROFİL EKLEME
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Profil ekle (çizgi/path)
     */
    profilEkle(noktalar: FikoVektor3D[]): void {
        if (noktalar.length >= 2) {
            this.profiller.push(noktalar.map(n => n.clone()));
            this.profil3DOlustur(noktalar);
        }
    }

    /**
     * Çoklu profil ekle
     */
    profillerEkle(profilListesi: FikoVektor3D[][]): void {
        for (const profil of profilListesi) {
            this.profilEkle(profil);
        }
    }

    /**
     * Dikdörtgen metin alanı tanımla
     */
    metinAlaniTanimla(x: number, y: number, genislik: number, yukseklik: number): void {
        // Metin alanı profili
        const profil: FikoVektor3D[] = [
            new FikoVektor3D(x, y, this.ustZ),
            new FikoVektor3D(x + genislik, y, this.ustZ),
            new FikoVektor3D(x + genislik, y + yukseklik, this.ustZ),
            new FikoVektor3D(x, y + yukseklik, this.ustZ),
            new FikoVektor3D(x, y, this.ustZ)
        ];
        this.profilEkle(profil);
    }

    // ═══════════════════════════════════════════════════════════════════
    // V-GRAVÜR DERİNLİK HESAPLAMA
    // ═══════════════════════════════════════════════════════════════════

    /**
     * V-kesici derinliğini çizgi genişliğine göre hesapla
     * Derinlik = Genişlik / (2 * tan(Açı/2))
     */
    vDerinlikHesapla(genislik: number): number {
        if (!this.vOtomatikDerinlik) {
            return this.gravurDerinlik;
        }

        const aciRadyan = (this.vAci / 2) * Math.PI / 180;
        const tanAci = Math.tan(aciRadyan);
        let derinlik = genislik / (2 * tanAci);

        // Min/max sınırla
        derinlik = Math.max(this.vMinDerinlik, Math.min(this.vMaxDerinlik, derinlik));

        return derinlik;
    }

    /**
     * V-kesici genişliğini derinliğe göre hesapla
     * Genişlik = 2 * Derinlik * tan(Açı/2)
     */
    vGenislikHesapla(derinlik: number): number {
        const aciRadyan = (this.vAci / 2) * Math.PI / 180;
        return 2 * derinlik * Math.tan(aciRadyan);
    }

    // ═══════════════════════════════════════════════════════════════════
    // TAKİM YOLU HESAPLAMA
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Ana takım yolu hesaplama metodu
     */
    takimYoluHesapla(): void {
        this.takimYoluNoktalar = [];
        this.toplamMesafe = 0;

        if (this.profiller.length === 0) return;

        // Profilleri optimize et
        this.profilleriOptimizeEt();

        // Güvenli Z'ye git
        const ilkProfil = this.profiller[0];
        if (ilkProfil.length > 0) {
            this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                ilkProfil[0].x,
                ilkProfil[0].y,
                this.guvenliZ,
                'rapid'
            ));
        }

        // Gravür tipine göre işlem
        switch (this.gravurTipi) {
            case 'CizgiGravur':
                this.cizgiGravurHesapla();
                break;
            case 'VGravur':
                this.vGravurHesapla();
                break;
            case 'DolguGravur':
                this.dolguGravurHesapla();
                break;
            case 'NoktaGravur':
                this.noktaGravurHesapla();
                break;
        }

        // Son güvenli Z
        if (this.takimYoluNoktalar.length > 0) {
            const son = this.takimYoluNoktalar[this.takimYoluNoktalar.length - 1];
            this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                son.x,
                son.y,
                this.guvenliZ,
                'rapid'
            ));
        }

        // 3D görüntü güncelle
        this.takimYolu3DGuncelle();
        this.hesaplananSure = this.sureHesapla();
    }

    // ═══════════════════════════════════════════════════════════════════
    // ÇİZGİ GRAVÜR (LINE ENGRAVING)
    // ═══════════════════════════════════════════════════════════════════

    private cizgiGravurHesapla(): void {
        for (let p = 0; p < this.profiller.length; p++) {
            const profil = this.profiller[p];
            if (profil.length < 2) continue;

            // Profili offsetle (gerekirse)
            let islemProfil = profil;
            if (this.cizgiOffset !== 'Merkez') {
                const ofsetMiktar = this.cizgiOffset === 'Sol' ? -this.cizgiGenislik / 2 : this.cizgiGenislik / 2;
                try {
                    const of = offsetHat(profil, ofsetMiktar, 'Dairesel');
                    islemProfil = (of && of.length >= 2) ? of : profil;   // offset başarısız → merkez çizgisi (çizgi gravüründe güvenli)
                } catch (e) {
                    islemProfil = profil;
                }
            }

            // Çoklu geçiş hesapla
            const gecisler = this.gecisleriHesapla(this.cizgiDerinlik);

            for (const gecisZ of gecisler) {
                // İlk noktaya rapid
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    islemProfil[0].x,
                    islemProfil[0].y,
                    this.ustZ + 1,
                    'rapid'
                ));

                // Giriş
                this.girisEkle(islemProfil[0], gecisZ);

                // Profil boyunca ilerle
                for (let i = 1; i < islemProfil.length; i++) {
                    this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                        islemProfil[i].x,
                        islemProfil[i].y,
                        gecisZ,
                        'cut',
                        this.ilerlemeGravur
                    ));
                }

                // Çıkış
                this.cikisEkle(islemProfil[islemProfil.length - 1], gecisZ);
            }
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // V-GRAVÜR (VCARVE)
    // ═══════════════════════════════════════════════════════════════════

    private vGravurHesapla(): void {
        for (let p = 0; p < this.profiller.length; p++) {
            const profil = this.profiller[p];
            if (profil.length < 2) continue;

            // V-gravürde her segment için ayrı derinlik hesaplanabilir
            // Basit implementasyon: tüm profil için sabit derinlik
            const derinlik = this.vOtomatikDerinlik
                ? this.vDerinlikHesapla(this.takim.cap)
                : this.gravurDerinlik;

            const gecisZ = this.ustZ - derinlik;

            // İlk noktaya rapid
            this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                profil[0].x,
                profil[0].y,
                this.ustZ + 1,
                'rapid'
            ));

            // Giriş
            this.girisEkle(profil[0], gecisZ);

            // Profil boyunca - V-kesici için yavaş ilerleme
            for (let i = 1; i < profil.length; i++) {
                // Segment genişliğine göre derinlik ayarla (gelişmiş VCarve)
                // Burada basit implementasyon kullanıyoruz
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    profil[i].x,
                    profil[i].y,
                    gecisZ,
                    'cut',
                    this.ilerlemeGravur
                ));
            }

            // Çıkış
            this.cikisEkle(profil[profil.length - 1], gecisZ);
        }

        // Flat bottom geçişi (varsa)
        if (this.vFlatTakim && this.vFlatDerinlik > 0) {
            // TODO: Flat bottom takım için ayrı takım yolu
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // DOLGU GRAVÜR (FILL ENGRAVING)
    // ═══════════════════════════════════════════════════════════════════

    private dolguGravurHesapla(): void {
        for (let p = 0; p < this.profiller.length; p++) {
            const profil = this.profiller[p];
            if (profil.length < 3) continue;

            // Çevre geçişi
            if (this.dolguCevre) {
                for (let c = 0; c < this.dolguCevreSayisi; c++) {
                    const cevreProfil = c === 0 ? profil : this.profilOffset(profil, -c * this.dolguAdim);
                    if (cevreProfil.length >= 3) {
                        this.profilGecisEkle(cevreProfil, this.ustZ - this.gravurDerinlik);
                    }
                }
            }

            // Dolgu deseni
            switch (this.dolguDeseni) {
                case 'Offset':
                    this.offsetDolguHesapla(profil);
                    break;
                case 'Raster':
                    this.rasterDolguHesapla(profil, this.dolguAci);
                    break;
                case 'CrossHatch':
                    this.rasterDolguHesapla(profil, this.dolguAci);
                    this.rasterDolguHesapla(profil, this.dolguAci + this.dolguCrossAci);
                    break;
                case 'Spiral':
                    this.spiralDolguHesapla(profil);
                    break;
            }
        }
    }

    private offsetDolguHesapla(profil: FikoVektor3D[]): void {
        let currentProfil = profil;
        const gecisZ = this.ustZ - this.gravurDerinlik;
        const startOffset = this.dolguCevre ? this.dolguCevreSayisi * this.dolguAdim : 0;
        let offset = startOffset + this.dolguAdim;

        while (true) {
            const yeniProfil = this.profilOffset(currentProfil, -offset);
            if (yeniProfil.length < 3) break;

            // Profil alanı çok küçükse dur
            const alan = this.profilAlaniHesapla(yeniProfil);
            if (alan < this.dolguAdim * this.dolguAdim) break;

            this.profilGecisEkle(yeniProfil, gecisZ);
            offset += this.dolguAdim * (1 - this.dolguOverlap / 100);

            // Güvenlik: çok fazla iterasyon
            if (offset > 1000) break;
        }
    }

    private rasterDolguHesapla(profil: FikoVektor3D[], aci: number): void {
        // Bounding box hesapla
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        for (const n of profil) {
            minX = Math.min(minX, n.x);
            maxX = Math.max(maxX, n.x);
            minY = Math.min(minY, n.y);
            maxY = Math.max(maxY, n.y);
        }

        const genislik = maxX - minX;
        const yukseklik = maxY - minY;
        const diyagonal = Math.sqrt(genislik * genislik + yukseklik * yukseklik);
        const merkezX = (minX + maxX) / 2;
        const merkezY = (minY + maxY) / 2;

        const aciRad = aci * Math.PI / 180;
        const gecisZ = this.ustZ - this.gravurDerinlik;

        // Raster çizgileri oluştur
        const adim = this.dolguAdim * (1 - this.dolguOverlap / 100);
        const satirSayisi = Math.ceil(diyagonal / adim);

        for (let i = -satirSayisi / 2; i <= satirSayisi / 2; i++) {
            const offset = i * adim;

            // Çizgi başlangıç ve bitiş (döndürülmüş)
            const cos = Math.cos(aciRad);
            const sin = Math.sin(aciRad);

            const x1 = merkezX + (-diyagonal) * cos - offset * sin;
            const y1 = merkezY + (-diyagonal) * sin + offset * cos;
            const x2 = merkezX + diyagonal * cos - offset * sin;
            const y2 = merkezY + diyagonal * sin + offset * cos;

            // Profil ile kesişim noktalarını bul
            const kesisimler = this.cizgiProfilKesisim(
                new FikoVektor3D(x1, y1, gecisZ),
                new FikoVektor3D(x2, y2, gecisZ),
                profil
            );

            // Kesişim çiftleri halinde çiz
            for (let k = 0; k < kesisimler.length - 1; k += 2) {
                if (k + 1 < kesisimler.length) {
                    const bas = kesisimler[k];
                    const son = kesisimler[k + 1];

                    // Rapid ile başlangıca
                    this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                        bas.x, bas.y, this.ustZ + 1, 'rapid'
                    ));

                    // Dalma
                    this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                        bas.x, bas.y, gecisZ, 'cut', this.ilerlemeDalma
                    ));

                    // Çizgi
                    this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                        son.x, son.y, gecisZ, 'cut', this.ilerlemeGravur
                    ));

                    // Çıkış
                    this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                        son.x, son.y, this.ustZ + 1, 'rapid'
                    ));
                }
            }
        }
    }

    private spiralDolguHesapla(profil: FikoVektor3D[]): void {
        // Offset dolgu ile aynı mantık, ama merkeze doğru spiral
        this.offsetDolguHesapla(profil);
    }

    // ═══════════════════════════════════════════════════════════════════
    // NOKTA GRAVÜR (STIPPLE)
    // ═══════════════════════════════════════════════════════════════════

    private noktaGravurHesapla(): void {
        for (const profil of this.profiller) {
            if (profil.length < 3) continue;

            // Bounding box
            let minX = Infinity, maxX = -Infinity;
            let minY = Infinity, maxY = -Infinity;
            for (const n of profil) {
                minX = Math.min(minX, n.x);
                maxX = Math.max(maxX, n.x);
                minY = Math.min(minY, n.y);
                maxY = Math.max(maxY, n.y);
            }

            const gecisZ = this.ustZ - this.noktaDerinlik;
            const noktalar: FikoVektor3D[] = [];

            // Desen tipine göre nokta oluştur
            switch (this.noktaDesen) {
                case 'Grid':
                    for (let y = minY; y <= maxY; y += this.noktaAralik) {
                        for (let x = minX; x <= maxX; x += this.noktaAralik) {
                            if (this.noktaProfilIcinde(x, y, profil)) {
                                noktalar.push(new FikoVektor3D(x, y, gecisZ));
                            }
                        }
                    }
                    break;

                case 'Hex':
                    let satirIdx = 0;
                    for (let y = minY; y <= maxY; y += this.noktaAralik * 0.866) {
                        const xOffset = satirIdx % 2 === 0 ? 0 : this.noktaAralik / 2;
                        for (let x = minX + xOffset; x <= maxX; x += this.noktaAralik) {
                            if (this.noktaProfilIcinde(x, y, profil)) {
                                noktalar.push(new FikoVektor3D(x, y, gecisZ));
                            }
                        }
                        satirIdx++;
                    }
                    break;

                case 'Random':
                    const alan = (maxX - minX) * (maxY - minY);
                    const noktaSayisi = Math.floor(alan / (this.noktaAralik * this.noktaAralik));
                    for (let i = 0; i < noktaSayisi; i++) {
                        const x = minX + Math.random() * (maxX - minX);
                        const y = minY + Math.random() * (maxY - minY);
                        if (this.noktaProfilIcinde(x, y, profil)) {
                            noktalar.push(new FikoVektor3D(x, y, gecisZ));
                        }
                    }
                    break;
            }

            // Noktaları optimize et (en kısa yol)
            const optimizeNoktalar = this.nearestNeighborSirala(noktalar);

            // Her nokta için işlem
            for (const nokta of optimizeNoktalar) {
                // Rapid
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    nokta.x, nokta.y, this.ustZ + 1, 'rapid'
                ));

                // Dalma
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    nokta.x, nokta.y, nokta.z, 'cut', this.ilerlemeDalma
                ));

                // Bekleme
                if (this.noktaBekleme > 0) {
                    this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                        nokta.x, nokta.y, nokta.z, 'dwell', 0, this.noktaBekleme
                    ));
                }

                // Çıkış
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    nokta.x, nokta.y, this.ustZ + 1, 'rapid'
                ));
            }
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // YARDIMCI METODLAR
    // ═══════════════════════════════════════════════════════════════════

    private gecisleriHesapla(toplamDerinlik: number): number[] {
        const gecisler: number[] = [];

        if (!this.cokluGecisEtkin || this.gecisDerinlik <= 0) {
            gecisler.push(this.ustZ - toplamDerinlik);
            return gecisler;
        }

        let kalan = toplamDerinlik - this.gecisPayi;
        let mevcutZ = this.ustZ;

        while (kalan > 0) {
            const gecisDerinlik = Math.min(this.gecisDerinlik, kalan);
            mevcutZ -= gecisDerinlik;
            gecisler.push(mevcutZ);
            kalan -= gecisDerinlik;
        }

        // Son geçiş (pay ile)
        if (this.gecisPayi > 0) {
            gecisler.push(this.ustZ - toplamDerinlik);
        }

        return gecisler;
    }

    private girisEkle(nokta: FikoVektor3D, hedefZ: number): void {
        switch (this.girisYontemi) {
            case 'Dikey':
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    nokta.x, nokta.y, hedefZ, 'cut', this.ilerlemeDalma
                ));
                break;

            case 'Rampa':
                const rampaUzunluk = Math.abs(hedefZ - this.ustZ) / Math.tan(this.girisRampaAcisi * Math.PI / 180);
                // Basitleştirilmiş rampa
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    nokta.x, nokta.y, hedefZ, 'cut', this.ilerlemeDalma
                ));
                break;

            case 'Yay':
                // Basitleştirilmiş yay girişi
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    nokta.x, nokta.y, hedefZ, 'cut', this.ilerlemeDalma
                ));
                break;
        }
    }

    private cikisEkle(nokta: FikoVektor3D, mevcutZ: number): void {
        switch (this.cikisYontemi) {
            case 'Dikey':
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    nokta.x, nokta.y, this.ustZ + 1, 'rapid'
                ));
                break;

            case 'Rampa':
            case 'Yay':
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    nokta.x, nokta.y, this.ustZ + 1, 'rapid'
                ));
                break;
        }
    }

    private profilGecisEkle(profil: FikoVektor3D[], z: number): void {
        if (profil.length < 2) return;

        // Rapid ile başlangıca
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            profil[0].x, profil[0].y, this.ustZ + 1, 'rapid'
        ));

        // Dalma
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            profil[0].x, profil[0].y, z, 'cut', this.ilerlemeDalma
        ));

        // Profil boyunca
        for (let i = 1; i < profil.length; i++) {
            this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                profil[i].x, profil[i].y, z, 'cut', this.ilerlemeGravur
            ));
        }

        // Çıkış
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            profil[profil.length - 1].x, profil[profil.length - 1].y, this.ustZ + 1, 'rapid'
        ));
    }

    private profilOffset(profil: FikoVektor3D[], miktar: number): FikoVektor3D[] {
        try {
            return offsetHat(profil, miktar, 'Dairesel', Math.abs(miktar) * 0.1, true);   // harf konturu KAPALI poligon (alan dolgusu)
        } catch (e) {
            return [];
        }
    }

    private profilAlaniHesapla(profil: FikoVektor3D[]): number {
        if (profil.length < 3) return 0;

        let alan = 0;
        for (let i = 0; i < profil.length - 1; i++) {
            alan += profil[i].x * profil[i + 1].y;
            alan -= profil[i + 1].x * profil[i].y;
        }
        return Math.abs(alan) / 2;
    }

    private noktaProfilIcinde(x: number, y: number, profil: FikoVektor3D[]): boolean {
        // Ray casting algoritması
        let inside = false;
        for (let i = 0, j = profil.length - 1; i < profil.length; j = i++) {
            const xi = profil[i].x, yi = profil[i].y;
            const xj = profil[j].x, yj = profil[j].y;

            if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
                inside = !inside;
            }
        }
        return inside;
    }

    private cizgiProfilKesisim(p1: FikoVektor3D, p2: FikoVektor3D, profil: FikoVektor3D[]): FikoVektor3D[] {
        const kesisimler: FikoVektor3D[] = [];

        for (let i = 0; i < profil.length - 1; i++) {
            const p3 = profil[i];
            const p4 = profil[i + 1];

            const kesisim = this.cizgiCizgiKesisim(p1, p2, p3, p4);
            if (kesisim) {
                kesisimler.push(kesisim);
            }
        }

        // X'e göre sırala
        kesisimler.sort((a, b) => {
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            if (Math.abs(dx) > Math.abs(dy)) {
                return a.x - b.x;
            } else {
                return a.y - b.y;
            }
        });

        return kesisimler;
    }

    private cizgiCizgiKesisim(p1: FikoVektor3D, p2: FikoVektor3D, p3: FikoVektor3D, p4: FikoVektor3D): FikoVektor3D | null {
        const x1 = p1.x, y1 = p1.y;
        const x2 = p2.x, y2 = p2.y;
        const x3 = p3.x, y3 = p3.y;
        const x4 = p4.x, y4 = p4.y;

        const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
        if (Math.abs(denom) < 0.0001) return null;

        const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
        const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

        if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
            return new FikoVektor3D(
                x1 + t * (x2 - x1),
                y1 + t * (y2 - y1),
                p1.z
            );
        }

        return null;
    }

    private nearestNeighborSirala(noktalar: FikoVektor3D[]): FikoVektor3D[] {
        if (noktalar.length < 2) return noktalar;

        const sirali: FikoVektor3D[] = [noktalar[0]];
        const kalan = new Set<number>();
        for (let i = 1; i < noktalar.length; i++) {
            kalan.add(i);
        }

        let mevcutIdx = 0;
        while (kalan.size > 0) {
            let enYakinIdx = -1;
            let enYakinMesafe = Infinity;

            for (const idx of kalan) {
                const dx = noktalar[idx].x - noktalar[mevcutIdx].x;
                const dy = noktalar[idx].y - noktalar[mevcutIdx].y;
                const mesafe = dx * dx + dy * dy;

                if (mesafe < enYakinMesafe) {
                    enYakinMesafe = mesafe;
                    enYakinIdx = idx;
                }
            }

            if (enYakinIdx >= 0) {
                sirali.push(noktalar[enYakinIdx]);
                kalan.delete(enYakinIdx);
                mevcutIdx = enYakinIdx;
            }
        }

        return sirali;
    }

    private profilleriOptimizeEt(): void {
        if (this.siralama === 'Secimsirasi') return;

        // Basit sıralama - ilk noktaya göre
        switch (this.siralama) {
            case 'SoldanSaga':
                this.profiller.sort((a, b) => (a[0]?.x || 0) - (b[0]?.x || 0));
                break;
            case 'YukaridanAsagi':
                this.profiller.sort((a, b) => (b[0]?.y || 0) - (a[0]?.y || 0));
                break;
            case 'EnKisaYol':
                // Nearest neighbor for profiles
                // TODO: Implement profile-level optimization
                break;
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // 3D GÖRÜNTÜLEME
    // ═══════════════════════════════════════════════════════════════════

    private profil3DOlustur(profil: FikoVektor3D[]): void {
        if (profil.length < 2) return;

        const points = profil.map(n => new THREE.Vector3(n.x, n.y, n.z));
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = new THREE.LineBasicMaterial({
            color: this.gravurTipiRenk(),
            linewidth: 2
        });
        const line = new THREE.Line(geometry, material);
        this.profilMesh.add(line);
    }

    private gravurTipiRenk(): number {
        switch (this.gravurTipi) {
            case 'CizgiGravur': return 0x00ff88;
            case 'VGravur': return 0xff8800;
            case 'DolguGravur': return 0x0088ff;
            case 'NoktaGravur': return 0xff0088;
            default: return 0xffffff;
        }
    }

    private takimYolu3DGuncelle(): void {
        // Eski mesh'leri temizle
        while (this.takimYoluMesh.children.length > 0) {
            const child = this.takimYoluMesh.children[0];
            this.takimYoluMesh.remove(child);
            if ((child as any).geometry) (child as any).geometry.dispose();
            if ((child as any).material) (child as any).material.dispose();
        }

        if (this.takimYoluNoktalar.length < 2) return;

        const rapidPoints: THREE.Vector3[] = [];
        const cutPoints: THREE.Vector3[] = [];

        for (let i = 0; i < this.takimYoluNoktalar.length - 1; i++) {
            const p1 = this.takimYoluNoktalar[i];
            const p2 = this.takimYoluNoktalar[i + 1];

            if (p2.tip === 'rapid') {
                rapidPoints.push(new THREE.Vector3(p1.x, p1.y, p1.z));
                rapidPoints.push(new THREE.Vector3(p2.x, p2.y, p2.z));
            } else if (p2.tip === 'cut') {
                cutPoints.push(new THREE.Vector3(p1.x, p1.y, p1.z));
                cutPoints.push(new THREE.Vector3(p2.x, p2.y, p2.z));
            }
        }

        // Rapid (sarı kesikli)
        if (rapidPoints.length >= 2) {
            const geom = new THREE.BufferGeometry().setFromPoints(rapidPoints);
            const mat = new THREE.LineDashedMaterial({
                color: 0xffff00,
                dashSize: 2,
                gapSize: 1
            });
            const line = new THREE.LineSegments(geom, mat);
            line.computeLineDistances();
            this.takimYoluMesh.add(line);
        }

        // Cut (tip renginde)
        if (cutPoints.length >= 2) {
            const geom = new THREE.BufferGeometry().setFromPoints(cutPoints);
            const mat = new THREE.LineBasicMaterial({
                color: this.gravurTipiRenk(),
                linewidth: 2
            });
            const line = new THREE.LineSegments(geom, mat);
            this.takimYoluMesh.add(line);
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // G-CODE ÇIKTISI
    // ═══════════════════════════════════════════════════════════════════

    gCodeUret(): string {
        let gcode = '';

        gcode += '%\n';
        gcode += `O${2000 + FikoAktiviteGravur.SAYAC} (${this.name})\n`;
        gcode += '(FikoYT CAM - Gravur Isleme)\n';
        gcode += `(Gravur Tipi: ${this.gravurTipi})\n`;
        gcode += `(Takim: ${this.takim.ad} D=${this.takim.cap}mm)\n`;
        gcode += `(Profil Sayisi: ${this.profiller.length})\n`;
        gcode += '\n';

        gcode += 'G21 (Metric)\n';
        gcode += 'G90 (Absolute)\n';
        gcode += 'G54 (Work Offset)\n';
        gcode += '\n';

        gcode += `S${this.devir} M03 (Spindle CW - High Speed for Engraving)\n`;
        gcode += '\n';

        gcode += `G00 Z${this.guvenliZ.toFixed(3)} (Safe Z)\n`;

        // Takım yolu noktaları
        for (let i = 0; i < this.takimYoluNoktalar.length; i++) {
            const n = this.takimYoluNoktalar[i];

            switch (n.tip) {
                case 'rapid':
                    gcode += `G00 X${n.x.toFixed(3)} Y${n.y.toFixed(3)} Z${n.z.toFixed(3)}\n`;
                    break;
                case 'cut':
                    gcode += `G01 X${n.x.toFixed(3)} Y${n.y.toFixed(3)} Z${n.z.toFixed(3)} F${n.ilerleme || this.ilerlemeGravur}\n`;
                    break;
                case 'dwell':
                    if (n.dwell) {
                        gcode += `G04 P${(n.dwell * 1000).toFixed(0)} (Dwell ${n.dwell}s)\n`;
                    }
                    break;
            }
        }

        gcode += '\n';
        gcode += `G00 Z${this.guvenliZ.toFixed(3)}\n`;
        gcode += 'M05 (Spindle Stop)\n';
        gcode += 'G28 G91 Z0\n';
        gcode += 'G28 X0 Y0\n';
        gcode += 'M30\n';
        gcode += '%\n';

        return gcode;
    }

    // ═══════════════════════════════════════════════════════════════════
    // SÜRE HESAPLAMA
    // ═══════════════════════════════════════════════════════════════════

    private sureHesapla(): number {
        let toplam = 0;
        this.toplamMesafe = 0;

        for (let i = 0; i < this.takimYoluNoktalar.length - 1; i++) {
            const p1 = this.takimYoluNoktalar[i];
            const p2 = this.takimYoluNoktalar[i + 1];
            const mesafe = Math.sqrt(
                Math.pow(p2.x - p1.x, 2) +
                Math.pow(p2.y - p1.y, 2) +
                Math.pow(p2.z - p1.z, 2)
            );

            this.toplamMesafe += mesafe;

            const hiz = p2.tip === 'rapid' ? this.hizRapid : (p2.ilerleme || this.ilerlemeGravur);
            toplam += mesafe / hiz;

            if (p2.tip === 'dwell' && p2.dwell) {
                toplam += p2.dwell / 60;
            }
        }

        return toplam;
    }

    // ═══════════════════════════════════════════════════════════════════
    // JSON SERİLEŞTİRME
    // ═══════════════════════════════════════════════════════════════════

    toJSON(): any {
        return {
            type: this.type,
            ad: this.name,
            gravurTipi: this.gravurTipi,
            profiller: this.profiller.map(p => p.map(n => ({ x: n.x, y: n.y, z: n.z }))),
            ustZ: this.ustZ,
            gravurDerinlik: this.gravurDerinlik,
            guvenliZ: this.guvenliZ,

            // Çizgi gravür
            cizgiDerinlik: this.cizgiDerinlik,
            cizgiGenislik: this.cizgiGenislik,
            cizgiOffset: this.cizgiOffset,

            // V-gravür
            vAci: this.vAci,
            vMaxDerinlik: this.vMaxDerinlik,
            vMinDerinlik: this.vMinDerinlik,
            vOtomatikDerinlik: this.vOtomatikDerinlik,

            // Dolgu gravür
            dolguDeseni: this.dolguDeseni,
            dolguAdim: this.dolguAdim,
            dolguAci: this.dolguAci,
            dolguOverlap: this.dolguOverlap,
            dolguCevre: this.dolguCevre,

            // Nokta gravür
            noktaAralik: this.noktaAralik,
            noktaDerinlik: this.noktaDerinlik,
            noktaDesen: this.noktaDesen,

            // Hız
            ilerlemeGravur: this.ilerlemeGravur,
            ilerlemeDalma: this.ilerlemeDalma,
            hizRapid: this.hizRapid,
            devir: this.devir,

            // Giriş/Çıkış
            girisYontemi: this.girisYontemi,
            cikisYontemi: this.cikisYontemi,

            // Optimizasyon
            siralama: this.siralama,
            cokluGecisEtkin: this.cokluGecisEtkin,
            gecisDerinlik: this.gecisDerinlik,

            // Metin
            metinIcerik: this.metinIcerik,
            fontAdi: this.fontAdi,
            fontSize: this.fontSize,

            // Takım
            takim: {
                ad: this.takim.ad,
                cap: this.takim.cap,
                takimTipi: this.takim.takimTipi
            }
        };
    }

    static fromJSON(json: any, sahne?: FikoSahne): FikoAktiviteGravur {
        const akt = new FikoAktiviteGravur(sahne);

        akt.name = json.ad || akt.name;
        akt.gravurTipi = json.gravurTipi || 'CizgiGravur';
        akt.profiller = (json.profiller || []).map((p: any[]) =>
            p.map(n => new FikoVektor3D(n.x, n.y, n.z))
        );
        akt.ustZ = json.ustZ ?? 0;
        akt.gravurDerinlik = json.gravurDerinlik ?? 0.5;
        akt.guvenliZ = json.guvenliZ ?? 5;

        // Çizgi
        akt.cizgiDerinlik = json.cizgiDerinlik ?? 0.3;
        akt.cizgiGenislik = json.cizgiGenislik ?? 0.5;
        akt.cizgiOffset = json.cizgiOffset || 'Merkez';

        // V-gravür
        akt.vAci = json.vAci ?? 60;
        akt.vMaxDerinlik = json.vMaxDerinlik ?? 3;
        akt.vMinDerinlik = json.vMinDerinlik ?? 0.1;
        akt.vOtomatikDerinlik = json.vOtomatikDerinlik ?? true;

        // Dolgu
        akt.dolguDeseni = json.dolguDeseni || 'Offset';
        akt.dolguAdim = json.dolguAdim ?? 0.3;
        akt.dolguAci = json.dolguAci ?? 0;
        akt.dolguOverlap = json.dolguOverlap ?? 10;
        akt.dolguCevre = json.dolguCevre ?? true;

        // Nokta
        akt.noktaAralik = json.noktaAralik ?? 1;
        akt.noktaDerinlik = json.noktaDerinlik ?? 0.2;
        akt.noktaDesen = json.noktaDesen || 'Grid';

        // Hız
        akt.ilerlemeGravur = json.ilerlemeGravur ?? 500;
        akt.ilerlemeDalma = json.ilerlemeDalma ?? 200;
        akt.hizRapid = json.hizRapid ?? 10000;
        akt.devir = json.devir ?? 12000;

        // Giriş/Çıkış
        akt.girisYontemi = json.girisYontemi || 'Dikey';
        akt.cikisYontemi = json.cikisYontemi || 'Dikey';

        // Optimizasyon
        akt.siralama = json.siralama || 'EnKisaYol';
        akt.cokluGecisEtkin = json.cokluGecisEtkin ?? false;
        akt.gecisDerinlik = json.gecisDerinlik ?? 0.2;

        // Metin
        akt.metinIcerik = json.metinIcerik || '';
        akt.fontAdi = json.fontAdi || 'Arial';
        akt.fontSize = json.fontSize ?? 10;

        // Takım
        if (json.takim) {
            akt.takim.ad = json.takim.ad || 'V-Kesici';
            akt.takim.cap = json.takim.cap ?? 3;
            akt.takim.takimTipi = json.takim.takimTipi || 'VKesici';
        }

        return akt;
    }

    // ═══════════════════════════════════════════════════════════════════
    // TEMİZLEME
    // ═══════════════════════════════════════════════════════════════════

    temizle(): void {
        this.profiller = [];
        this.takimYoluNoktalar = [];

        while (this.profilMesh.children.length > 0) {
            const child = this.profilMesh.children[0];
            this.profilMesh.remove(child);
            if ((child as any).geometry) (child as any).geometry.dispose();
            if ((child as any).material) (child as any).material.dispose();
        }

        while (this.takimYoluMesh.children.length > 0) {
            const child = this.takimYoluMesh.children[0];
            this.takimYoluMesh.remove(child);
            if ((child as any).geometry) (child as any).geometry.dispose();
            if ((child as any).material) (child as any).material.dispose();
        }
    }

    dispose(): void {
        this.temizle();
        if (typeof (this as any).parent?.remove === 'function') {
            (this as any).parent.remove(this);
        }
    }
}
